import React, { useState, useEffect } from 'react';
import { FaDownload, FaCheck, FaTrash, FaSpinner } from 'react-icons/fa';
import {
  downloadTrack,
  downloadAlbum,
  removeDownloadedTrack,
  removeDownloadedAlbum,
  isTrackDownloaded,
  getDownloadedByAlbum
} from '../utils/offlineManager';

// Bouton de téléchargement pour une piste
export function TrackDownloadBtn({ track, albumInfo, size = 'small' }) {
  const [downloaded, setDownloaded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    checkStatus();
  }, [track.id]);

  const checkStatus = async () => {
    try {
      const result = await isTrackDownloaded(track.id);
      setDownloaded(result);
    } catch (e) {
      console.error('Erreur vérification offline:', e);
    }
  };

  const handleClick = async (e) => {
    e.stopPropagation();
    if (downloading) return;

    if (downloaded) {
      try {
        await removeDownloadedTrack(track);
        setDownloaded(false);
      } catch (err) {
        console.error('Erreur suppression:', err);
      }
      return;
    }

    setDownloading(true);
    setProgress(0);
    try {
      await downloadTrack(track, albumInfo, (p) => setProgress(p));
      setDownloaded(true);
    } catch (err) {
      console.error('Erreur téléchargement:', err);
      alert('Erreur lors du téléchargement');
    } finally {
      setDownloading(false);
      setProgress(0);
    }
  };

  const btnClass = size === 'small' ? 'dl-btn dl-btn-sm' : 'dl-btn dl-btn-lg';

  return (
    <button
      className={`${btnClass} ${downloaded ? 'dl-done' : ''} ${downloading ? 'dl-loading' : ''}`}
      onClick={handleClick}
      title={downloaded ? 'Supprimer le téléchargement' : downloading ? `${progress}%` : 'Télécharger pour hors-ligne'}
    >
      {downloading ? (
        <>
          <FaSpinner className="dl-spin" />
          {size !== 'small' && <span>{progress}%</span>}
        </>
      ) : downloaded ? (
        <>
          <FaCheck />
          {size !== 'small' && <span>Hors-ligne</span>}
        </>
      ) : (
        <>
          <FaDownload />
          {size !== 'small' && <span>Télécharger</span>}
        </>
      )}
    </button>
  );
}

// Bouton de téléchargement pour un album complet
export function AlbumDownloadBtn({ album, tracks }) {
  const [downloaded, setDownloaded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [trackInfo, setTrackInfo] = useState('');

  useEffect(() => {
    checkStatus();
  }, [album.id, tracks]);

  const checkStatus = async () => {
    try {
      if (!tracks || tracks.length === 0) return;
      const dlTracks = await getDownloadedByAlbum(album.id);
      setDownloaded(dlTracks.length >= tracks.length);
    } catch (e) {
      console.error('Erreur vérification album offline:', e);
    }
  };

  const handleClick = async (e) => {
    e.stopPropagation();
    if (downloading) return;

    if (downloaded) {
      if (!window.confirm(`Supprimer ${album.title} du stockage hors-ligne ?`)) return;
      try {
        await removeDownloadedAlbum(album.id);
        setDownloaded(false);
      } catch (err) {
        console.error('Erreur suppression album:', err);
      }
      return;
    }

    if (!tracks || tracks.length === 0) {
      alert('Aucune piste à télécharger');
      return;
    }

    setDownloading(true);
    setProgress(0);
    try {
      await downloadAlbum(album, tracks, (p, current, total) => {
        setProgress(p);
        setTrackInfo(`${current}/${total}`);
      });
      setDownloaded(true);
    } catch (err) {
      console.error('Erreur téléchargement album:', err);
      alert('Erreur lors du téléchargement');
    } finally {
      setDownloading(false);
      setProgress(0);
      setTrackInfo('');
    }
  };

  return (
    <button
      className={`dl-btn dl-btn-album ${downloaded ? 'dl-done' : ''} ${downloading ? 'dl-loading' : ''}`}
      onClick={handleClick}
      title={downloaded ? 'Supprimer du hors-ligne' : downloading ? `${progress}% (${trackInfo})` : 'Télécharger l\'album'}
    >
      {downloading ? (
        <>
          <FaSpinner className="dl-spin" />
          <span>{progress}% {trackInfo && `(${trackInfo})`}</span>
        </>
      ) : downloaded ? (
        <>
          <FaCheck />
          <span>Hors-ligne</span>
        </>
      ) : (
        <>
          <FaDownload />
          <span>Télécharger</span>
        </>
      )}
    </button>
  );
}

export default { TrackDownloadBtn, AlbumDownloadBtn };
